import { TDestinationCredentials } from '@/core/apis/config_api/config_api_types';
import { TEventSpecificData } from '@/core/constants/event_types/base_mable_event';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { TMableProduct } from '@/core/constants/event_types/extended_mable_event';

import { GtagEvent, GtagEventParams, GtagEventType, GtagItem } from './types';

import { DestEventBuilder } from '../../destination_event_builder/destination_event_builder';

export class GtagTransformer {
  private static readonly createEvent = (
    name: string,
    params: GtagEventParams,
  ): GtagEvent => ({ event_name: name, event_params: params });
  private static readonly clean = (
    obj: Record<string, any>,
  ): Record<string, any> =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
  private static readonly createGtagItem = (
    p: TMableProduct,
    destinationConfig: TDestinationCredentials,
  ): GtagItem =>
    this.clean({
      currency: GtagTransformer.getCurrency(p),
      discount: p.totalDiscount || 0,
      item_brand: p.br,
      item_category: p.cat,
      item_id: DestEventBuilder.getProductCatalogItemIdForMableProduct(
        p,
        destinationConfig,
      ),
      item_name: p.nam,
      item_variant: p.var,
      price: Number(p.prc) || null,
      quantity: p.qty,
    }) as GtagItem;
  private static readonly getCurrency = (obj: any): string =>
    obj?.currency || obj?.cur || 'EUR';
  private static readonly getValue = (obj: any): number =>
    Number(obj?.total?.price || obj?.prc || 0);

  static transform(
    mableEvent: TMableEvent,
    destinationConfig: TDestinationCredentials,
    debug = false,
  ): GtagEvent {
    const { en: eventName, esd = {}, ii, pd, pids } = mableEvent;
    const baseParams: GtagEventParams = this.clean({
      page_location: pd.dl,
      page_title: pd.dt,
      user_id: ii,
    }) as GtagEventParams;
    if (pids.gclid?.[0]) {
      baseParams.gclid = pids.gclid[-1] as string;
    }
    if (debug) {
      baseParams.debug = '1';
      baseParams.debug_mode = '1';
    }

    const {
      amount,
      cart: cart,
      collection,
      currency,
      lead_source,
      oid,
      pay_methods,
      product,
      products,
    } = esd;

    if (
      eventName === EMableEventName.CUSTOM_EVENT &&
      esd?.cen &&
      (esd.cen as any) !== ''
    ) {
      const customEventName = esd.cen as unknown as GtagEventType;
      if (oid) {
        return this.createOrderEvent(
          cart,
          oid,
          baseParams,
          destinationConfig,
          customEventName,
        );
      } else if (cart) {
        return this.createCartEvent(
          customEventName,
          cart,
          baseParams,
          destinationConfig,
        );
      } else if (product) {
        return this.createProductEvent(
          customEventName,
          product,
          baseParams,
          destinationConfig,
        );
      }
      return this.createEvent(esd.cen, baseParams);
    }

    const eventMap: Record<EMableEventName, () => GtagEvent> = {
      [EMableEventName.CHECKOUT_STARTED]: () =>
        this.createCartEvent(
          GtagEventType.BEGIN_CHECKOUT,
          cart,
          baseParams,
          destinationConfig,
        ),
      [EMableEventName.COLLECTION_VIEWED]: () =>
        this.createCollectionEvent(
          collection,
          products,
          baseParams,
          destinationConfig,
        ),
      [EMableEventName.CUSTOM_EVENT]: () =>
        this.createCustomEvent(esd, baseParams),
      [EMableEventName.LEAD]: () =>
        this.createLeadEvent(amount, currency, lead_source, baseParams),
      [EMableEventName.ORDER_COMPLETED]: () =>
        this.createOrderEvent(cart, oid, baseParams, destinationConfig),
      [EMableEventName.PAGE_VIEWED]: () =>
        this.createEvent(GtagEventType.PAGE_VIEW, baseParams),
      [EMableEventName.PAYMENT_INFO_ADDED]: () =>
        this.createPaymentEvent(
          cart,
          pay_methods,
          baseParams,
          destinationConfig,
        ),
      [EMableEventName.POST_PURCHASE_SURVEY]: () =>
        this.createEvent(GtagEventType.CUSTOM, baseParams),
      [EMableEventName.PRODUCT_ADDED]: () =>
        this.createProductAddedEvent(
          GtagEventType.ADD_TO_CART,
          product,
          amount,
          baseParams,
          destinationConfig,
        ),
      [EMableEventName.PRODUCT_VIEWED]: () =>
        this.createProductEvent(
          GtagEventType.VIEW_ITEM,
          product,
          baseParams,
          destinationConfig,
        ),
    };

    return eventMap[eventName]?.() || this.createEvent(eventName, baseParams);
  }

  private static createProductEvent(
    eventName: GtagEventType,
    product: TMableProduct | undefined,
    baseParams: GtagEventParams,
    destinationConfig: TDestinationCredentials,
  ): GtagEvent {
    return this.createEvent(
      eventName,
      this.clean({
        ...baseParams,
        currency: this.getCurrency(product),
        items: product
          ? [this.createGtagItem(product, destinationConfig)]
          : null,
        value: this.getValue(product),
      } as Record<string, any>) as GtagEventParams,
    );
  }

  private static createProductAddedEvent(
    eventName: GtagEventType,
    product: TMableProduct | undefined,
    amount: number | undefined,
    baseParams: GtagEventParams,
    destinationConfig: TDestinationCredentials,
  ): GtagEvent {
    return this.createEvent(
      eventName,
      this.clean({
        ...baseParams,
        currency: this.getCurrency(product),
        items: product
          ? [this.createGtagItem(product, destinationConfig)]
          : null,
        value: (amount || 1) * (Number(product?.prc) || 0),
      } as Record<string, any>) as GtagEventParams,
    );
  }

  private static createCartEvent(
    eventName: GtagEventType,
    cart: any,
    baseParams: GtagEventParams,
    destinationConfig: TDestinationCredentials,
  ): GtagEvent {
    return this.createEvent(
      eventName,
      this.clean({
        ...baseParams,
        currency: this.getCurrency(cart),
        items: cart?.products?.map((p: TMableProduct) =>
          this.createGtagItem(p, destinationConfig),
        ),
        value: this.getValue(cart),
      }) as GtagEventParams,
    );
  }

  private static createPaymentEvent(
    cart: any,
    pay_methods: string[] | undefined,
    baseParams: GtagEventParams,
    destinationConfig: TDestinationCredentials,
  ): GtagEvent {
    return this.createEvent(
      GtagEventType.ADD_PAYMENT_INFO,
      this.clean({
        ...this.createCartEvent(
          GtagEventType.ADD_PAYMENT_INFO,
          cart,
          baseParams,
          destinationConfig,
        ).event_params,
        payment_type: pay_methods,
      }) as GtagEventParams,
    );
  }

  private static createOrderEvent(
    cart: any,
    oid: string | undefined,
    baseParams: GtagEventParams,
    destinationConfig: TDestinationCredentials,
    eventName: GtagEventType = GtagEventType.PURCHASE,
  ): GtagEvent {
    return this.createEvent(
      eventName,
      this.clean({
        ...this.createCartEvent(eventName, cart, baseParams, destinationConfig)
          .event_params,
        shipping: cart?.total?.shipping,
        tax: cart?.total?.tax,
        transaction_id: oid,
      }) as GtagEventParams,
    );
  }

  private static createLeadEvent(
    amount: number | undefined,
    currency: string | undefined,
    _lead_source: string | undefined,
    baseParams: GtagEventParams,
  ): GtagEvent {
    return this.createEvent(
      GtagEventType.GENERATE_LEAD,
      this.clean({
        ...baseParams,
        currency: currency || 'EUR',
        value: amount || 0,
      }) as GtagEventParams,
    );
  }

  private static createCollectionEvent(
    collection: any,
    products: TMableProduct[] | undefined,
    baseParams: GtagEventParams,
    destinationConfig: TDestinationCredentials,
  ): GtagEvent {
    return this.createEvent(
      GtagEventType.VIEW_ITEM_LIST,
      this.clean({
        ...baseParams,
        item_list_id: collection?.id,
        item_list_name: collection?.name,
        items: products?.map((p: TMableProduct) =>
          this.createGtagItem(p, destinationConfig),
        ),
      }) as GtagEventParams,
    );
  }

  private static createCustomEvent(
    esd: TEventSpecificData,
    baseParams: GtagEventParams,
  ): GtagEvent {
    const { cart: cart, oid, pay_methods, product } = esd;
    return this.createEvent(
      GtagEventType.CUSTOM,
      this.clean({
        ...baseParams,
        currency: this.getCurrency(cart || product),
        payment_type: pay_methods,
        transaction_id: oid,
        value: this.getValue(cart || product),
      }) as GtagEventParams,
    );
  }
}
